/**
 * Shared helpers for plan progress.
 *
 * The updatePlanProgress tool returns only numbers and an optional note (no message object):
 * - completedStepIndices: 0-based step indices to mark as completed (checkmarks)
 * - inProgressStepIndices: 0-based step indices currently in progress (spinner)
 * - skippedStepIndices: 0-based step indices that were skipped (not applicable or intentionally not done)
 * - note: optional short note for the user (e.g. "Created PROJ-123")
 */

/**
 * Extracts and validates 0-based step indices from an array.
 * Returns undefined if the value is not a non-empty array of non-negative integers.
 */
function parseStepIndices(val: unknown): number[] | undefined {
  if (!Array.isArray(val)) {
    return undefined;
  }
  const numbers = val.filter(
    (v): v is number => typeof v === 'number' && Number.isInteger(v) && v >= 0,
  );
  return numbers.length > 0 ? numbers : undefined;
}

/**
 * Parses the raw updatePlanProgress tool block input into typed fields.
 * Safe to call with unknown; returns all-undefined when input is invalid.
 *
 * @param raw - Tool block input (typically content.input from a toolUse block)
 * @returns completedStepIndices, inProgressStepIndices, skippedStepIndices, and optional note
 */
export function parseUpdatePlanProgress(raw: unknown): {
  completedStepIndices: number[] | undefined;
  inProgressStepIndices: number[] | undefined;
  skippedStepIndices: number[] | undefined;
  note: string | undefined;
} {
  if (typeof raw !== 'object' || raw === null) {
    return {
      completedStepIndices: undefined,
      inProgressStepIndices: undefined,
      skippedStepIndices: undefined,
      note: undefined,
    };
  }
  const obj = raw as Record<string, unknown>;
  const completedStepIndices =
    'completedStepIndices' in obj ? obj.completedStepIndices : undefined;
  const inProgressStepIndices =
    'inProgressStepIndices' in obj ? obj.inProgressStepIndices : undefined;
  const skippedStepIndices =
    'skippedStepIndices' in obj ? obj.skippedStepIndices : undefined;
  const noteVal = 'note' in obj ? obj.note : undefined;
  return {
    completedStepIndices: parseStepIndices(completedStepIndices),
    inProgressStepIndices: parseStepIndices(inProgressStepIndices),
    skippedStepIndices: parseStepIndices(skippedStepIndices),
    note: typeof noteVal === 'string' ? noteVal : undefined,
  };
}
